/**
 * surgicalTempo.js - 2026-10-18 (Tab 3 review, Dr. Tatar: shift mode linked to surge and MASCAL)
 *
 * Operations TEMPO of the surgical teams (normal / surge / crisis), derived each day from the load of the facility, and the
 * numbers that go with it. Built on SURGICAL_FATIGUE_MODEL of surgicalCapacity.js (shift length, shifts a day, days on before
 * rest, longest duration, performance curve by hours of work).
 *
 * STATUS of the values (labelled in the app): the shift recommendations and the performance curve are in surgicalCapacity.js
 * without a cited source -> EDUCATED GUESS; the capacity ratio of surge to normal comes from SURGICAL_CAPACITY_BY_MTF
 * (Role 2 Basic: 12 against 8 procedures a day = 1.5) -> DERIVED from that table; the sleep debt loss and the load thresholds
 * below are EDUCATED GUESS.
 */
import { SURGICAL_FATIGUE_MODEL, SURGICAL_CAPACITY_BY_MTF } from './surgicalCapacity';

export const SURGICAL_TEMPOS = ['NORMAL', 'SURGE', 'CRISIS'];

/** The recommendation block of SURGICAL_FATIGUE_MODEL for each tempo. */
export const TEMPO_RECOMMENDATION_KEY = Object.freeze({
  NORMAL: 'normal_operations',
  SURGE: 'surge_operations',
  CRISIS: 'crisis_operations'
});

export const SURGICAL_TEMPO_STATUS = Object.freeze({
  status: 'EDUCATED_GUESS',
  basis: 'Shift lengths, days on before rest and the performance curve: SURGICAL_FATIGUE_MODEL (no cited source). Capacity gain of surge: Role 2 Basic maximum / normal procedures a day. Sleep debt and load thresholds: guess.'
});

/** Surge capacity of a team relative to its normal capacity (Role 2 Basic: max_procedures_per_day / procedures_per_day). */
export const SURGE_CAPACITY_RATIO = (() => {
  const r2b = SURGICAL_CAPACITY_BY_MTF && SURGICAL_CAPACITY_BY_MTF.ROLE_2_BASIC;
  if (!r2b || !(r2b.procedures_per_day > 0) || !(r2b.max_procedures_per_day >= r2b.procedures_per_day)) {
    throw new Error('surgicalTempo: SURGICAL_CAPACITY_BY_MTF.ROLE_2_BASIC needs procedures_per_day and max_procedures_per_day');
  }
  return r2b.max_procedures_per_day / r2b.procedures_per_day;
})();

/** Capacity of one team in each tempo, relative to normal (crisis: the same throughput as surge, with the worse performance). */
export const TEMPO_CAPACITY_RATIO = Object.freeze({ NORMAL: 1, SURGE: SURGE_CAPACITY_RATIO, CRISIS: SURGE_CAPACITY_RATIO });

/** Daily load (demand and backlog against the normal capacity) from which each tempo starts. EDUCATED GUESS. */
export const LOAD_RATIO_SURGE_FROM = 1.0;
export const LOAD_RATIO_CRISIS_FROM = SURGE_CAPACITY_RATIO;   // the surge capacity itself is exceeded

/** Sleep debt: each day beyond 'days on before rest' in a non-normal tempo costs this share of performance; floor below. EDUCATED GUESS. */
export const SLEEP_DEBT_LOSS_PER_DAY = 0.05;
export const SLEEP_DEBT_FLOOR = 0.60;
/** A day at the normal tempo gives back this many days of non-normal tempo: the days a team may stay on in a surge before it rests
 * (SURGICAL_FATIGUE_MODEL.SHIFT_RECOMMENDATIONS.surge_operations.days_on_before_rest = 3). EDUCATED GUESS. */
export const NORMAL_DAY_RECOVERS_DAYS = 3;

/** Died of wounds: +2 percent for every 10 percent of lost performance (SURGICAL_FATIGUE_MODEL.FATIGUE_IMPACT). */
export const FATIGUE_MORTALITY_PER_10_PERCENT = SURGICAL_FATIGUE_MODEL.FATIGUE_IMPACT.mortality_increase_per_10_percent_fatigue;

/** Highest tempo the planner allows (policy). */
export const DEFAULT_MAX_TEMPO = 'CRISIS';

/**
 * Average surgeon performance over a shift of the given length: the mean of the performance curve from hour 0 to the end of
 * the shift (the curve value at the END of the shift is the quality of the last hour, not of the shift).
 */
export function averageShiftPerformance(shiftHours) {
  const curve = SURGICAL_FATIGUE_MODEL.PERFORMANCE_CURVE;
  if (!(shiftHours > 0)) throw new Error(`surgicalTempo.averageShiftPerformance: shift length must be above 0 (got: ${shiftHours})`);
  let area = 0;
  for (let i = 0; i < curve.length - 1; i += 1) {
    const a = curve[i];
    const b = curve[i + 1];
    if (shiftHours <= a.hours) break;
    const to = Math.min(shiftHours, b.hours);
    const perfAtTo = a.performance + (b.performance - a.performance) * ((to - a.hours) / (b.hours - a.hours));
    area += ((a.performance + perfAtTo) / 2) * (to - a.hours);
  }
  const last = curve[curve.length - 1];
  if (shiftHours > last.hours) area += last.performance * (shiftHours - last.hours);
  return area / shiftHours;
}
